import { Photo } from '../types';
import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { getOptimizedImageUrl } from '../utils/imageOptimization';

interface PhotoCardProps {
  photo: Photo;
  onViewDetails: (photo: Photo) => void;
  index: number;
  flexGrow?: number;
  height?: string;
  indexInRow?: number;
}

export function PhotoCard({ photo, onViewDetails, index, flexGrow = 1, height = '55vh', indexInRow = 0 }: PhotoCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: false, margin: "-8%" });
  const isPriority = index < 5;
  const delay = indexInRow * 0.1;
  const isWide = flexGrow >= 1.5;

  return (
    <motion.div
      ref={ref}
      className="group relative cursor-pointer overflow-hidden"
      style={{ flex: `${flexGrow} 1 0%`, height }}
      onClick={() => onViewDetails(photo)}
      initial={{ opacity: 0, y: 50, scale: 0.94 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 50, scale: 0.94 }}
      transition={{
        duration: 0.9,
        delay: isInView ? delay : 0,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      {/* Full color image with parallax-like hover zoom */}
      <motion.img
        src={getOptimizedImageUrl(photo.imageUrl, isWide ? 'preview' : 'thumbnail', photo.thumbnailUrl)}
        alt={`${photo.title} — ${photo.category} fine art photography print by Keyiscreation`}
        loading={isPriority ? "eager" : "lazy"}
        decoding="async"
        width={isWide ? 800 : 400}
        height={isWide ? 533 : 400}
        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
      />

      {/* Subtle bottom gradient for text legibility */}
      <div 
        className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" 
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 40%, transparent 100%)' }} 
      />

      {/* Very subtle ambient vignette — always visible */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ background: 'radial-gradient(ellipse at center, transparent 65%, rgba(0,0,0,0.12) 100%)' }} 
      />

      {/* Availability badge */}
      {photo.sizes && photo.sizes.length > 0 && (() => {
        const minAvailable = Math.min(...photo.sizes.map(s => s.available ?? 10));
        const isSoldOut = minAvailable <= 0;
        if (isSoldOut) {
          return (
            <div className="absolute top-3 right-3 px-4 py-2 text-[11px] tracking-[2.5px] uppercase font-light border border-red-700/60 text-red-400/80">
              Sold Out
            </div>
          );
        }
        if (minAvailable < 10) {
          return (
            <div className="absolute top-3 right-3 px-4 py-2 text-[11px] tracking-[2.5px] uppercase font-light border border-white/25 text-white/60">
              {minAvailable} Remaining
            </div>
          );
        }
        return null;
      })()}

      {/* Title on hover */}
      <div className="absolute inset-0 flex items-end justify-start opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-5 md:p-8">
        <div className="text-white">
          <h3 className={`text-white mb-1 drop-shadow-lg ${isWide ? 'text-base md:text-lg' : 'text-sm md:text-base'}`}>
            {photo.title}
          </h3>
          <p className="text-xs text-white/75 tracking-wider uppercase drop-shadow-md">{photo.category}</p>
        </div>
      </div>
    </motion.div>
  );
}